import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, LogOut, Menu, Search, X } from 'lucide-react';
import WishlistIcon from './common/WishlistIcon';
import CartIcon from './common/CartIcon';
import OptimizedImage from './common/OptimizedImage';
import UserMenu from './ui/UserMenu';
import { useAuth } from '../context/AuthContext';

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();
  const { user, profile, logout } = useAuth();
  const searchInputRef = useRef(null);
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/');

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 18);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveMenu(null);
    setSearchOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!location.hash) return undefined;
    const targetId = decodeURIComponent(location.hash.slice(1));
    const scrollToTarget = () => {
      const target = document.getElementById(targetId);
      if (!target) return false;
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      observer.disconnect();
      window.clearTimeout(timeout);
      return true;
    };
    let timeout;
    const observer = new MutationObserver(scrollToTarget);
    if (!scrollToTarget()) {
      observer.observe(document.body, { childList: true, subtree: true });
      timeout = window.setTimeout(() => observer.disconnect(), 5000);
    }
    return () => {
      observer.disconnect();
      window.clearTimeout(timeout);
    };
  }, [location.pathname, location.hash, location.key]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', closeOnEscape);
    };
  }, [isOpen]);

  useEffect(() => {
    if (searchOpen) searchInputRef.current?.focus();
  }, [searchOpen]);

  useEffect(() => {
    const closeMenus = (event) => {
      if (!event.target.closest('.cab-nav__menu-wrap') && !event.target.closest('.cab-nav__account')) setActiveMenu(null);
    };
    document.addEventListener('click', closeMenus);
    return () => document.removeEventListener('click', closeMenus);
  }, []);

  useEffect(() => {
    const closeActiveMenu = () => setActiveMenu(null);
    document.addEventListener('pointerleave', closeActiveMenu);
    window.addEventListener('blur', closeActiveMenu);
    return () => {
      document.removeEventListener('pointerleave', closeActiveMenu);
      window.removeEventListener('blur', closeActiveMenu);
    };
  }, []);

  if (isAdminRoute) return null;

  const navItems = [
    { path: '/shop', label: 'Shop' },
    { path: '/best-sellers', label: 'Jerseys', menu: 'jerseys' },
    { path: '/shop', label: 'Collection', menu: 'clothing' },
    { path: '/#player-shop-title', label: 'Shop by Player' },
    { path: '/#customizer-title', label: 'Create Your Jersey' },
    { path: '/membership', label: 'Membership' },
  ];

  const megaMenus = {
    jerseys: {
      kicker: 'Maillots officiels',
      title: 'Le maillot\ndu Nord.',
      description: 'Les couleurs de Bizerte, pensées pour le match et la ville.',
      links: ['Domicile', 'Extérieur', 'Gardien', 'Collection 2026', 'Éditions limitées'],
      image: '/assets/home 1.png',
    },
    clothing: {
      kicker: 'Vestiaire CAB',
      title: 'L’esprit\ndu club.',
      description: 'Une garde-robe premium pour porter CAB au quotidien.',
      links: ['Vestes', 'Sweats', 'T-shirts', 'Pantalons', 'Training'],
      image: '/assets/créer maillot/away kit.jpg',
    },
    accessories: {
      kicker: 'Les essentiels',
      title: 'Chaque détail\ncompte.',
      description: 'Les pièces qui prolongent la passion bien au-delà du stade.',
      links: ['Écharpes', 'Casquettes', 'Sacs', 'Collectibles', 'Cadeaux CAB'],
      image: '/assets/3rd kit.png',
    },
  };

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    if (query.trim()) navigate(`/shop?search=${encodeURIComponent(query.trim())}`);
  };

  const closeMobile = () => setIsOpen(false);
  const handleLogout = async () => {
    await logout();
    closeMobile();
    navigate('/');
  };

  const isItemActive = (item) => item.menu
    ? activeMenu === item.menu || (item.menu === 'jerseys' && location.pathname === item.path)
    : location.pathname === item.path;

  const renderMegaMenu = (menuKey) => {
    const menu = megaMenus[menuKey];
    return (
      <AnimatePresence>
        {activeMenu === menuKey && (
          <motion.div
            className="cab-mega-menu"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.22, ease: 'easeOut' }}
            onMouseLeave={() => setActiveMenu(null)}
          >
            <div className="cab-mega-menu__inner">
              <div className="cab-mega-menu__intro">
                <span className="cab-nav__eyebrow">{menu.kicker}</span>
                <h2>{menu.title.split('\n').map((line) => <span key={line}>{line}</span>)}</h2>
                <p>{menu.description}</p>
                <Link to="/shop" onClick={() => setActiveMenu(null)} className="cab-mega-menu__cta">Voir toute la collection <span>→</span></Link>
              </div>
              <div className="cab-mega-menu__links">
                {menu.links.map((link) => <Link key={link} to="/shop" onClick={() => setActiveMenu(null)}>{link}<span>↗</span></Link>)}
              </div>
              <div className="cab-mega-menu__feature">
                <OptimizedImage src={menu.image} alt="CAB collection" sizes="260px" />
                <span>Édition officielle · CAB</span>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  return (
    <>
      <motion.nav
        initial={{ y: -80 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        className={`cab-nav fixed top-0 left-0 right-0 z-50 ${scrolled ? 'cab-nav--scrolled' : ''}`}
      >
        <div className="cab-container">
          <div className="cab-nav__bar">
            <Link to="/" className="cab-nav__brand" aria-label="CAB Store accueil">
              <span className="cab-nav__crest"><OptimizedImage src="/assets/cab-logo.png" alt="" sizes="36px" loading="eager" /></span>
              <span className="cab-nav__wordmark"><strong>CAB STORE</strong><small>OFFICIAL CLUB SHOP</small></span>
            </Link>

            <div className="cab-nav__links">
              {navItems.map((item) => (
                <div key={`${item.path}-${item.label}`} className={`cab-nav__menu-wrap ${item.className || ''}`}>
                  {item.menu ? (
                    <button type="button" onClick={(event) => { event.stopPropagation(); setActiveMenu(activeMenu === item.menu ? null : item.menu); }} onMouseEnter={() => setActiveMenu(item.menu)} className={`cab-nav__link ${isItemActive(item) ? 'is-active' : ''}`} aria-expanded={activeMenu === item.menu}>
                      {item.label}<ChevronDown size={12} />
                    </button>
                  ) : (
                    <Link
                  key={item.path}
                  to={item.path}
                  className={`cab-nav__link ${isItemActive(item) ? 'is-active' : ''}`}
                >
                  {item.label}
                </Link>)}
                  {item.menu && renderMegaMenu(item.menu)}
                </div>
              ))}
            </div>

            <div className="cab-nav__actions">
              <button type="button" className="cab-nav__icon-button cab-nav__search-trigger" onClick={() => setSearchOpen((value) => !value)} aria-label="Rechercher" aria-expanded={searchOpen}><Search size={18} /></button>
              <div className="cab-nav__account">
                <UserMenu
                  user={user ? {
                    name: profile?.name || user.user_metadata?.display_name || user.email?.split('@')[0] || 'Membre CAB',
                    email: user.email || '',
                    avatarSrc: profile?.avatar_url || user.user_metadata?.avatar_url || user.user_metadata?.picture,
                  } : null}
                  onSignOut={handleLogout}
                />
              </div>
              <WishlistIcon />
              <CartIcon />
              <button
                type="button"
                aria-label="Open navigation menu"
                onClick={() => setIsOpen((value) => !value)}
                className="cab-nav__icon-button cab-nav__mobile-trigger"
              >
                {isOpen ? <X size={18} /> : <Menu size={18} />}
              </button>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {searchOpen && <motion.form className="cab-nav__search-panel" onSubmit={handleSearchSubmit} initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}><div className="cab-container"><Search size={19} /><input ref={searchInputRef} value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Rechercher une pièce, une collection..." aria-label="Recherche produit" /><button type="button" onClick={() => { setQuery(''); setSearchOpen(false); }} aria-label="Fermer la recherche"><X size={18} /></button></div></motion.form>}
        </AnimatePresence>
      </motion.nav>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, x: '100%' }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: '100%' }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            className="cab-mobile-menu fixed inset-0 z-40 lg:hidden"
            role="dialog"
            aria-modal="true"
            aria-label="Navigation"
          >
            <div className="cab-mobile-menu__head">
              <Link to="/" onClick={closeMobile} className="cab-mobile-menu__brand" aria-label="CAB Store accueil">
                <span className="cab-nav__crest"><OptimizedImage src="/assets/cab-logo.png" alt="" sizes="36px" loading="eager" /></span>
                <span className="cab-nav__wordmark"><strong>CAB STORE</strong><small>OFFICIAL CLUB SHOP</small></span>
              </Link>
              <button type="button" onClick={closeMobile} className="cab-nav__icon-button" aria-label="Fermer le menu"><X size={18} /></button>
            </div>
            <nav className="cab-mobile-menu__links" aria-label="Menu principal">
              {navItems.map((item, index) => (
                <Link
                  key={`${item.path}-${item.label}`}
                  to={item.path}
                  onClick={closeMobile}
                  className={isItemActive(item) ? 'is-active' : ''}
                >
                  <span>{item.label}</span><small>{String(index + 1).padStart(2, '0')}</small>
                </Link>
              ))}
            </nav>
            <div className="cab-mobile-menu__footer">
              {user ? <>
                <Link to="/profile" onClick={closeMobile} className="cab-mobile-menu__account-link">Mon compte <span>↗</span></Link>
                <Link to="/orders" onClick={closeMobile} className="cab-mobile-menu__account-link">Mes commandes <span>↗</span></Link>
                <button type="button" onClick={handleLogout} className="cab-mobile-menu__account-link"><span className="cab-mobile-menu__logout-label"><LogOut size={15} /> Déconnexion</span><span>↗</span></button>
              </> : <>
                <Link to="/login" onClick={closeMobile} className="cab-mobile-menu__account-link">Se connecter <span>↗</span></Link>
                <Link to="/register" onClick={closeMobile} className="cab-mobile-menu__account-link">Créer un compte <span>↗</span></Link>
              </>}
              <Link to="/wishlist" onClick={closeMobile} className="cab-mobile-menu__wishlist">Wishlist <span>→</span></Link>
              <Link to="/shop" onClick={closeMobile} className="cab-mobile-menu__cta">Voir la collection <span>→</span></Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
